/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

export const SYMBOL_INFO_CACHE_STORAGE_KEY = 'paxia:symbol-info-cache:v2';
export const SYMBOL_INFO_REVALIDATE_AFTER_MS = 7 * 24 * 60 * 60 * 1000;

const MAX_ENTRIES = 100;
const memoryCache = new Map();
let hydrated = false;

const buildKey = (accountId, symbol) =>
  `${accountId || ''}:${String(symbol || '').toUpperCase()}`;

const isCacheEntry = (entry) => (
  entry
  && typeof entry === 'object'
  && Number.isFinite(Number(entry.ts))
  && entry.data
  && typeof entry.data === 'object'
  && !Array.isArray(entry.data)
);

const getBrowserStorage = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
};

const trimCache = () => {
  while (memoryCache.size > MAX_ENTRIES) {
    memoryCache.delete(memoryCache.keys().next().value);
  }
};

const persistCache = () => {
  const storage = getBrowserStorage();
  if (!storage) return;

  try {
    storage.setItem(
      SYMBOL_INFO_CACHE_STORAGE_KEY,
      JSON.stringify({ version: 1, entries: Array.from(memoryCache.entries()) }),
    );
  } catch {
    // Storage can be unavailable or full; the in-memory cache remains usable.
  }
};

const hydrateCache = () => {
  if (hydrated) return;
  hydrated = true;

  const storage = getBrowserStorage();
  if (!storage) return;

  try {
    const payload = JSON.parse(storage.getItem(SYMBOL_INFO_CACHE_STORAGE_KEY) || 'null');
    const entries = payload?.version === 1 && Array.isArray(payload.entries)
      ? payload.entries
      : [];

    entries.forEach(([key, entry]) => {
      if (typeof key === 'string' && isCacheEntry(entry)) {
        memoryCache.set(key, { ts: Number(entry.ts), data: entry.data });
      }
    });
    trimCache();
  } catch {
    try { storage.removeItem(SYMBOL_INFO_CACHE_STORAGE_KEY); } catch { /* ignore */ }
  }
};

export const getCachedSymbolInfo = (accountId, symbol, now = Date.now()) => {
  hydrateCache();
  const key = buildKey(accountId, symbol);
  const entry = memoryCache.get(key);
  if (!isCacheEntry(entry)) return null;

  const age = Math.max(0, now - Number(entry.ts));
  return {
    data: entry.data,
    state: age <= SYMBOL_INFO_REVALIDATE_AFTER_MS ? 'fresh' : 'stale',
  };
};

export const setCachedSymbolInfo = (accountId, symbol, data, now = Date.now()) => {
  if (!accountId || !symbol || !data || typeof data !== 'object' || Array.isArray(data)) {
    return;
  }

  hydrateCache();
  const key = buildKey(accountId, symbol);
  memoryCache.delete(key);
  memoryCache.set(key, { ts: now, data });
  trimCache();
  persistCache();
};

export const clearSymbolInfoCache = () => {
  hydrated = true;
  memoryCache.clear();
  const storage = getBrowserStorage();
  try { storage?.removeItem(SYMBOL_INFO_CACHE_STORAGE_KEY); } catch { /* ignore */ }
};

export const createSymbolInfoLoader = (fetchSymbolInfo) => {
  const inflight = new Map();
  let generation = 0;

  const refresh = (accountId, symbol) => {
    const key = buildKey(accountId, symbol);
    const existing = inflight.get(key);
    if (existing) return existing;

    const requestGeneration = generation;
    let request;
    request = Promise.resolve()
      .then(() => fetchSymbolInfo(accountId, symbol))
      .then((data) => {
        if (requestGeneration === generation) {
          setCachedSymbolInfo(accountId, symbol, data);
        }
        return data;
      })
      .finally(() => {
        if (inflight.get(key) === request) inflight.delete(key);
      });
    inflight.set(key, request);
    return request;
  };

  return {
    get(accountId, symbol) {
      const cached = getCachedSymbolInfo(accountId, symbol);
      if (cached?.state === 'fresh') return Promise.resolve(cached.data);
      if (cached?.state === 'stale') {
        refresh(accountId, symbol).catch(() => undefined);
        return Promise.resolve(cached.data);
      }
      return refresh(accountId, symbol);
    },
    clear() {
      generation += 1;
      inflight.clear();
      clearSymbolInfoCache();
    },
  };
};
